// The stage: the 3D engine scene, driven by the view state and the audio.
//
// It draws the model group named after the engine, or the kinematic fixture
// until that exists (models.ts), from the camera in the view state or the
// model's first fixed camera; cuts it open with the view state's cutaway; and
// moves it with the heard crank angle, as a strobe view or at true speed
// (display-clock.ts). The bar over the canvas switches the fixed cameras
// (data/views/<group>.toml) and the motion display, and names the part under
// a click; the pointer moves the camera (app/viewport.ts). Replaces the WebGL
// probe of #29.
//
// The heard angle comes from the voice's telemetry and the audio clock
// (sync/heard-angle.ts, #33). While a voice runs, the poses come from its
// module's pose_at on a main-thread instance of its wasm, with the heard slow
// state (sync/synced-pose.ts); otherwise from the model's pose table.

import "./scene.css";
import { defineFeature } from "../registry";
import { el } from "../app/controls/dom";
import type { ViewState } from "../state/view-state";
import { EngineScene } from "./engine-scene";
import { cameraViews, modelFor, modelUrl, partLabel, poseFor, type CameraView, type IndexedModel } from "./models";
import { strings } from "./strings";
import { Viewport } from "../app/viewport";
import { HeardAngle, newHeardSample } from "../sync/heard-angle";
import { SyncedPose, poseInstanceFor } from "../sync/synced-pose";
import type { PoseInstance } from "../audio/pose-instance";

const DEG = Math.PI / 180;

function rendererName(gl: WebGL2RenderingContext): string {
  const info = gl.getExtension("WEBGL_debug_renderer_info");
  return String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
}

export default defineFeature({
  id: "scene",
  title: strings.label,
  slot: "stage",
  mount({ root, state, audio }) {
    const stage = el("div", { class: "scene-stage" });
    const canvas = el("canvas", { tabindex: 0, "aria-label": strings.label, title: strings.pickHint });
    const cameraGroup = el("div", { class: "scene-group", role: "group", "aria-label": strings.cameras },
      el("span", { class: "scene-group-label" }, strings.cameras));
    const strobe = el("button", { type: "button", "data-display": "strobe", title: strings.displayHint.strobe }, strings.display.strobe);
    const trueSpeed = el("button", { type: "button", "data-display": "true-speed", title: strings.displayHint.trueSpeed }, strings.display.trueSpeed);
    const displayGroup = el("div", { class: "scene-group", role: "group", "aria-label": strings.display.label },
      el("span", { class: "scene-group-label" }, strings.display.label), strobe, trueSpeed);
    const picked = el("output", { class: "scene-picked", "aria-live": "polite", hidden: true });
    const bar = el("div", { class: "scene-bar" }, cameraGroup, displayGroup, picked);
    stage.append(canvas, bar);
    root.append(stage);

    let scene: EngineScene;
    try {
      // preserveDrawingBuffer lets tests read the canvas after the frame is presented.
      scene = new EngineScene({ canvas, preserveDrawingBuffer: true, display: state.state.time.display });
    } catch (error) {
      console.warn("scene: no WebGL2", error);
      root.dataset["gl"] = "unavailable";
      stage.replaceChildren(el("p", { class: "scene-message" }, strings.noWebGl));
      return;
    }
    root.dataset["gl"] = "webgl2";
    root.dataset["renderer"] = rendererName(scene.renderer.getContext() as WebGL2RenderingContext);
    root.dataset["caps"] = scene.capMode;
    let model: IndexedModel | undefined;
    let cameras: CameraView[] = [];
    let loaded = false;
    let disposed = false;
    const heard = new HeardAngle();
    const sample = newHeardSample();
    let pose: SyncedPose | null = null;
    let instance: PoseInstance | null = null;
    let instanceFor: object | null = null;

    // Follow the voice: its telemetry and clock for the heard angle, its wasm for the poses.
    const followAudio = (): void => {
      const engine = audio.engine;
      heard.attach(engine);
      if (engine === instanceFor) return;
      instanceFor = engine;
      pose?.use(null);
      instance?.free();
      instance = null;
      root.dataset["pose"] = "table";
      if (engine === null || !model) return;
      const m = model;
      poseInstanceFor(engine)
        .then((made) => {
          if (made === null) return;
          if (disposed || instanceFor !== engine) {
            made.free();
            return;
          }
          const missing = made.info.module === m.module ? (pose?.use(made) ?? ["(no model)"]) : [`module ${made.info.module}`];
          if (missing.length > 0) {
            console.warn(`scene: the voice's pose_at does not fit ${m.group} (${missing.join(", ")}); the pose table stays`);
            made.free();
            return;
          }
          instance = made;
          root.dataset["pose"] = "pose_at";
        })
        .catch((error: unknown) => console.warn("scene: no main-thread pose instance", error));
    };
    const offAudio = audio.onStatus(followAudio);

    const applyCamera = (s: ViewState): void => {
      const camera = s.camera ?? cameras[0]?.camera;
      if (camera) scene.setCamera(camera);
      for (const b of cameraGroup.querySelectorAll<HTMLButtonElement>("button")) {
        const view = cameras.find((c) => c.id === b.dataset["view"]);
        const current = s.camera ?? cameras[0]?.camera;
        b.setAttribute("aria-pressed", String(!!view && JSON.stringify(view.camera) === JSON.stringify(current)));
      }
    };
    const applyDisplay = (s: ViewState): void => {
      scene.setDisplay(s.time.display);
      strobe.setAttribute("aria-pressed", String(s.time.display === "strobe"));
      trueSpeed.setAttribute("aria-pressed", String(s.time.display === "true-speed"));
    };
    const apply = (s: ViewState, previous?: ViewState): void => {
      if (!previous || s.camera !== previous.camera) applyCamera(s);
      if (!previous || s.cutaway !== previous.cutaway) scene.setCutaway(s.cutaway);
      if (!previous || s.time !== previous.time) applyDisplay(s);
    };

    const buildCameraButtons = (): void => {
      cameraGroup.querySelectorAll("button").forEach((b) => b.remove());
      for (const view of cameras) {
        const b = el("button", { type: "button", "data-view": view.id }, view.title);
        b.addEventListener("click", () => {
          state.set(view.cutaway ? { camera: view.camera, cutaway: view.cutaway } : { camera: view.camera });
        });
        cameraGroup.append(b);
      }
      cameraGroup.hidden = cameras.length === 0;
    };

    for (const b of [strobe, trueSpeed]) {
      b.addEventListener("click", () => state.update((s) => void (s.time.display = b.dataset["display"] as ViewState["time"]["display"])));
    }

    const viewport = new Viewport({
      element: canvas,
      current: () => state.state.camera ?? cameras[0]?.camera ?? null,
      preview: (camera) => scene.setCamera(camera),
      commit: (camera) => void state.set({ camera }),
      reset: () => {
        state.set({ camera: null });
        applyCamera(state.state); // null to null is no change, but a preview may need undoing
      },
    });

    canvas.addEventListener("click", (event) => {
      if (!model) return;
      const r = canvas.getBoundingClientRect();
      const hit = scene.pick(((event.clientX - r.left) / r.width) * 2 - 1, -((event.clientY - r.top) / r.height) * 2 + 1);
      picked.hidden = hit === null;
      picked.textContent = hit ? `${partLabel(model, hit.part)}${hit.onCap ? ` (${strings.cutFace})` : ""}` : "";
      root.dataset["picked"] = hit ? hit.part.object.name : "";
    });

    const resize = (): void => {
      const w = Math.max(1, canvas.clientWidth);
      const h = Math.max(1, canvas.clientHeight);
      scene.resize(w, h);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    let raf = 0;
    let frames = 0;
    const tick = (now: number): void => {
      raf = requestAnimationFrame(tick);
      if (!loaded) return;
      const t = state.state.time;
      if (t.paused && t.angle !== null) {
        scene.clock.reset(t.angle * DEG);
        scene.frame(t.angle * DEG, 0, now);
      } else {
        heard.read(now, sample);
        pose?.setSlow(sample.slow, sample.slowCount);
        scene.frame(sample.theta, sample.omega, now);
      }
      frames++;
      if (frames % 15 === 1) {
        root.dataset["frames"] = String(frames);
        root.dataset["angle"] = (scene.clock.angle / DEG).toFixed(1);
        root.dataset["heard"] = (sample.theta / DEG).toFixed(1);
        root.dataset["sync"] = sample.path;
      }
    };

    const off = state.subscribe((s, previous) => {
      if (loaded) apply(s, previous);
    });

    model = modelFor(state.state.engine);
    if (!model) {
      stage.append(el("p", { class: "scene-message" }, strings.noModel));
    } else {
      const m = model;
      root.dataset["model"] = m.group;
      cameras = cameraViews(m.group);
      buildCameraButtons();
      void poseFor(m)
        .then((table) => {
          pose = new SyncedPose(table);
          return scene.load(modelUrl(m), pose);
        })
        .then(() => {
          if (disposed) return;
          loaded = true;
          apply(state.state);
          root.dataset["loaded"] = "true";
          instanceFor = null;
          followAudio();
        })
        .catch((error: unknown) => {
          console.error("scene: the model did not load", error);
          root.dataset["error"] = String(error);
          stage.append(el("p", { class: "scene-message" }, strings.loadFailed));
        });
    }
    raf = requestAnimationFrame(tick);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      off();
      offAudio();
      viewport.dispose();
      heard.dispose();
      instance?.free();
      scene.dispose();
    };
  },
});
